
import React, { useState, useEffect } from 'react';
import HeroSlider from '../components/Home/HeroSlider';
import ConsultationCalculatorSection from '../components/Home/ConsultationCalculatorSection';
import LocationsCarousel from '../components/Home/LocationsCarousel';
import StrengthsSection from '../components/Home/StrengthsSection';
import Homepage from '../components/Home/Homepage';
import { FaCommentDots, FaSun, FaTimes, FaPaperPlane } from 'react-icons/fa';
import './Home.css'; // We'll add some CSS

const Home = () => {
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState('');
  const [leadCaptured, setLeadCaptured] = useState(false);
  const [awaitingLeadInfo, setAwaitingLeadInfo] = useState(false);
  const [leadStep, setLeadStep] = useState(0);
  const [userData, setUserData] = useState({ name: '', phone: '', city: '' });
  const [messageCount, setMessageCount] = useState(0);
  const [quickReplies, setQuickReplies] = useState([]);

  // Initialize chat
  useEffect(() => {
    if (isChatOpen && messages.length === 0) {
      initChat();
    }
  }, [isChatOpen]);

  const initChat = () => {
    addMessage("Hi 👋 Welcome to Blue Sky Energy! How can we help you today?", 'bot');
    setTimeout(() => {
      addMessage("Choose an option to get started:", 'bot');
      setQuickReplies([
        "🏠 Residential Solar", 
        "🏢 Commercial Solar", 
        "💰 Pricing & Subsidy", 
        "📍 Our Service Areas"
      ]);
    }, 800);
  };

  const addMessage = (text, sender) => {
    const time = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const newMessage = {
      text,
      sender,
      time,
      id: Date.now()
    };
    setMessages(prev => [...prev, newMessage]);
    if (sender === 'user') {
      setMessageCount(prev => prev + 1);
    }
  };

  // Keyword Logic
  const matchKeywords = (input) => {
    const text = input.toLowerCase();
    if (text.includes('human') || text.includes('call') || text.includes('talk')) {
      return "I'd be happy to connect you with our team! 📞\n\nCall us directly: (+91) 80879 32436\nOr chat on WhatsApp:\n👉 https://wa.me/918087932436";
    }
    if (text.includes('commercial') || text.includes('business')) {
      return "☀️ Commercial Solar Solutions\n\nROI typically within 3-5 years. We're National Award Winners! Would you like to know about pricing?";
    }
    if (text.includes('home') || text.includes('residential')) {
      return "🏠 Residential Solar Solutions\n\nEMI options available. As India's #1 Rooftop Player, we guarantee quality. Interested in a home quote?";
    }
    if (text.includes('price') || text.includes('cost')) {
      return "💰 Typical costs:\n• 3kW: ₹1.8L - ₹2.2L\n• 5kW: ₹2.8L - ₹3.5L\n\nShall I collect your details for a personalized quote?";
    }
    if (text.includes('wind')) {
      return "🌬️ Wind Energy Solutions\n\nWe've installed 993 MW wind capacity across India! Dependable as the sun.";
    }
    if (text.includes('award') || text.includes('national')) {
      return "🏆 National Award Winner - Blue Sky Energy!\n\nWe're recognized for excellence in green energy solutions across India.";
    }
    return "I understand you're interested in solar solutions! I can help with pricing, installation process, wind energy, or connecting you with our award-winning team.";
  };

  // Lead Capture Logic
  const handleLeadCapture = (input) => {
    if (leadStep === 0) {
      setUserData(prev => ({ ...prev, name: input }));
      setLeadStep(1);
      addMessage(`Nice to meet you, ${input}! 😊\n\nCould you please share your phone number?`, 'bot');
    } else if (leadStep === 1) {
      setUserData(prev => ({ ...prev, phone: input }));
      setLeadStep(2);
      addMessage("Great! And which city are you from?", 'bot');
    } else if (leadStep === 2) {
      setUserData(prev => ({ ...prev, city: input }));
      setLeadCaptured(true);
      setAwaitingLeadInfo(false);
      saveLead({ ...userData, city: input });
      addMessage(`Perfect! Thank you ${userData.name}! ✅\n\nOur award-winning team will contact you within 24 hours.`, 'bot');
    }
  };

  const saveLead = (data) => {
    const leadId = `lead_${Date.now()}`;
    localStorage.setItem(leadId, JSON.stringify({...data, timestamp: new Date()}));
    console.log("Lead Saved:", data);
  };

  const handleSend = () => {
    const text = userInput.trim();
    if (!text) return;

    addMessage(text, 'user');
    setUserInput('');
    setQuickReplies([]);

    setTimeout(() => {
      if (awaitingLeadInfo) {
        handleLeadCapture(text);
      } else {
        const response = matchKeywords(text);
        addMessage(response, 'bot');

        // Trigger Lead Capture after 3 interactions
        if (!leadCaptured && messageCount >= 2) {
          setTimeout(() => {
            addMessage("I'd love to help you further! 📋\n\nCan I get your name to assist you better?", 'bot');
            setAwaitingLeadInfo(true);
          }, 1500);
        }
      }
    }, 600);
  };

  const handleQuickReply = (option) => {
    setUserInput(option);
    setTimeout(() => {
      handleSend();
    }, 100);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSend();
    }
  };

  return (
    <div className="home-page">
      {/* Responsive CSS Styles - Blue Theme */}
      <style>
        {`
          /* Color Palette */
          :root {
            --color-deep-teal: #061221;
            --color-dark-navy: #123249;
            --color-midnight: #2D5A74;
            --color-slate: #447793;
            --color-frost: #DDE8ED;
            --color-white: #ffffff;
            --gradient-primary: linear-gradient(135deg, #447793 0%, #2D5A74 100%);
            --gradient-hover: linear-gradient(135deg, #2D5A74 0%, #123249 100%);
            --shadow-sm: 0 2px 15px rgba(6, 18, 33, 0.08);
            --shadow-md: 0 4px 20px rgba(6, 18, 33, 0.12);
            --shadow-lg: 0 10px 30px rgba(6, 18, 33, 0.15);
          }

          /* Mobile Responsive Styles */
          @media (max-width: 768px) {
            .home-page {
              overflow-x: hidden;
            }
            
            .hero-slider-section {
              margin-top: 60px !important;
            }
            
            .consultation-calculator-section .container {
              padding: 0 15px !important;
            }
            
            .cta-section .container {
              padding: 0 15px !important;
            }
            
            .cta-buttons {
              flex-direction: column !important;
              gap: 15px !important;
              width: 100% !important;
            }
            
            .cta-buttons button {
              width: 100% !important;
              margin: 0 !important;
            }
            
            .chat-toggle-btn {
              width: 50px !important;
              height: 50px !important;
              font-size: 18px !important;
              bottom: 15px !important;
              right: 15px !important;
            }
            
            .chat-window {
              width: calc(100vw - 40px) !important;
              right: 20px !important;
              left: 20px !important;
              bottom: 70px !important;
              height: 450px !important;
              max-height: 60vh !important;
            }
            
            .chat-header {
              padding: 12px 15px !important;
            }
            
            .chat-header h3 {
              font-size: 14px !important;
            }
            
            .chat-header p {
              font-size: 11px !important;
            }
            
            .chat-messages {
              padding: 12px !important;
            }
            
            .msg {
              max-width: 85% !important;
              padding: 10px 14px !important;
              font-size: 13px !important;
            }
            
            .msg-time {
              font-size: 10px !important;
            }
            
            .quick-replies {
              padding: 10px !important;
              gap: 6px !important;
            }
            
            .reply-btn {
              font-size: 11px !important;
              padding: 6px 10px !important;
            }
            
            .chat-input {
              padding: 10px 12px !important;
              font-size: 13px !important;
            }
            
            .send-btn {
              width: 40px !important;
              height: 40px !important;
              font-size: 14px !important;
            }
            
            .footer-text {
              font-size: 10px !important;
            }
            
            .header-icon {
              width: 35px !important;
              height: 35px !important;
              font-size: 16px !important;
            }
            
            .location-section-wrapper {
              padding: 20px 15px !important;
            }
            
            .strengths-section-wrapper {
              padding: 30px 15px !important;
            }
            
            .home-page-wrapper {
              padding: 20px 15px !important;
            }
          }
          
          @media (max-width: 480px) {
            .chat-window {
              width: calc(100vw - 20px) !important;
              right: 10px !important;
              left: 10px !important;
              height: 400px !important;
            }
            
            .chat-toggle-btn {
              width: 45px !important;
              height: 45px !important;
              font-size: 16px !important;
              bottom: 10px !important;
              right: 10px !important;
            }
            
            .msg {
              max-width: 90% !important;
              padding: 8px 12px !important;
              font-size: 12px !important;
            }
            
            .quick-replies {
              flex-wrap: wrap !important;
            }
            
            .reply-btn {
              flex: 1 !important;
              min-width: calc(50% - 6px) !important;
              font-size: 10px !important;
              padding: 5px 8px !important;
              text-align: center !important;
            }
            
            .cta-content h2 {
              font-size: 1.5rem !important;
            }
            
            .cta-content p {
              font-size: 0.9rem !important;
            }
            
            .btn {
              padding: 10px 16px !important;
              font-size: 0.9rem !important;
            }
          }
          
          @media (min-width: 769px) and (max-width: 1024px) {
            .chat-window {
              width: 350px !important;
              height: 500px !important;
            }
            
            .chat-toggle-btn {
              width: 55px !important;
              height: 55px !important;
            }
            
            .cta-buttons {
              flex-direction: row !important;
              justify-content: center !important;
            }
            
            .cta-buttons button {
              width: auto !important;
            }
          }
          
          @media (min-width: 1025px) {
            .chat-window {
              width: 380px !important;
              height: 550px !important;
            }
            
            .chat-toggle-btn {
              width: 60px !important;
              height: 60px !important;
            }
          }
          
          /* Additional responsive adjustments */
          @media (max-width: 768px) {
            .container {
              max-width: 100% !important;
              padding: 0 15px !important;
            }
            
            .btn-lg {
              padding: 12px 20px !important;
              font-size: 1rem !important;
            }
          }
          
          @media (max-width: 576px) {
            .cta-content {
              padding: 20px !important;
            }
            
            .cta-content h2 {
              font-size: 1.3rem !important;
              margin-bottom: 10px !important;
            }
            
            .cta-content p {
              font-size: 0.85rem !important;
              margin-bottom: 20px !important;
            }
          }
          
          /* Chatbot animation adjustments for mobile */
          @media (max-width: 768px) {
            .chat-window {
              transition: all 0.3s ease !important;
            }
            
            .chat-window.hidden {
              opacity: 0 !important;
              visibility: hidden !important;
              transform: translateY(20px) !important;
            }
            
            .chat-window:not(.hidden) {
              opacity: 1 !important;
              visibility: visible !important;
              transform: translateY(0) !important;
            }
          }
        `}
      </style>

      {/* Chatbot Component - Blue Theme */}
      <div className={`chatbot-container ${isChatOpen ? 'open' : ''}`}>
        {/* Chat Toggle Button */}
        <button 
          className="chat-toggle-btn animate-pulse"
          onClick={() => setIsChatOpen(!isChatOpen)}
        >
          <FaCommentDots />
        </button>

        {/* Chat Window */}
        <div className={`chat-window ${isChatOpen ? '' : 'hidden'}`}>
          {/* Chat Header */}
          <div className="chat-header">
            <div className="header-info">
              <div className="header-icon">
                <FaSun />
              </div>
              <div>
                <h3>Blue Sky Energy</h3>
                <p>National Award Winner</p>
              </div>
            </div>
            <button 
              className="close-chat-btn"
              onClick={() => setIsChatOpen(false)}
            >
              <FaTimes />
            </button>
          </div>

          {/* Quick Replies */}
          {quickReplies.length > 0 && (
            <div className="quick-replies">
              {quickReplies.map((option, index) => (
                <button
                  key={index}
                  className="reply-btn"
                  onClick={() => handleQuickReply(option)}
                >
                  {option}
                </button>
              ))}
            </div>
          )}

          {/* Chat Messages */}
          <div className="chat-messages">
            {messages.map((msg) => (
              <div 
                key={msg.id} 
                className={`msg ${msg.sender}`}
              >
                {msg.text}
                <span className="msg-time">{msg.time}</span>
              </div>
            ))}
          </div>

          {/* Chat Input */}
          <div className="chat-input-area">
            <div className="input-wrapper">
              <input
                type="text"
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="chat-input"
              />
              <button 
                className="send-btn"
                onClick={handleSend}
              >
                <FaPaperPlane />
              </button>
            </div>
            <p className="footer-text">Powered by Blue Sky Energy</p>
          </div>
        </div>
      </div>

      {/* Your Existing Website Content */}
      {/* Hero Slider Section */}
      <section className="hero-slider-section">
        <HeroSlider />
      </section>
      
      {/* Consultation Form + Calculator Section (50/50 split) */}
      <section className="consultation-calculator-section">
        <div className="container">
          <ConsultationCalculatorSection />
        </div>
      </section>
      
      {/* Strengths Section */}
      <section className="strengths-section-wrapper">
        <StrengthsSection />
      </section>

      <section className='home-page-wrapper'>
        <Homepage />
      </section>
      
      {/* Map Section */}
      <section className="location-section-wrapper">
        <LocationsCarousel />
      </section>
      
      {/* CTA Section - Blue Theme */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to Power Your Future?</h2>
            <p>Get a free consultation from our solar experts</p>
            <div className="cta-buttons">
              <button className="btn btn-primary btn-lg">
                Get Free Quote
              </button>
              <button className="btn btn-outline btn-lg">
                Call Now: (+91) 80879 32436
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;